import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Printer,
  Phone,
  MessageCircle,
  Trash2,
  Loader2,
  Save,
} from "lucide-react";
import { api, formatRp, statusMeta, getStatusDisplay, formatDate, COURIERS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import PhotoGallery from "@/components/PhotoGallery";
import AuditLog from "@/components/AuditLog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const nextStatusFlow = { masuk: "proses", proses: "selesai_ok", selesai_ok: "diambil", selesai_gagal: "diambil", selesai: "diambil" };
const isCompleted = (s) => s === "selesai" || s === "selesai_ok" || s === "selesai_gagal";

export default function ServiceDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const canEditDiag = can.editDiagnosis(role);
  const canEditCost = can.editFinalCost(role);
  const canAssign = can.editAssignedTech(role);
  const canDelete = can.deleteService(role);
  const [svc, setSvc] = useState(null);
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const techPromise = canAssign
      ? api.get("/technicians").catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });
    Promise.all([api.get(`/services/${id}`), techPromise])
      .then(([s, t]) => {
        setSvc(s.data);
        setTechs(t.data);
        setForm({
          status: s.data.status,
          final_cost: s.data.final_cost || "",
          diagnosis: s.data.diagnosis || "",
          notes: s.data.notes || "",
          assigned_tech_id: s.data.assigned_tech_id || "unassigned",
          pickup_method: s.data.pickup_method || "",
          courier: s.data.courier || "",
          tracking_number: s.data.tracking_number || "",
        });
      })
      .catch(() => toast.error("Gagal memuat data"));
  }, [id, canAssign]);

  if (!svc)
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status: form.status, notes: form.notes };
      if (canEditCost) payload.final_cost = Number(form.final_cost || 0);
      if (canEditDiag) payload.diagnosis = form.diagnosis;
      if (canAssign) {
        payload.assigned_tech_id =
          form.assigned_tech_id === "unassigned" ? null : form.assigned_tech_id;
      }
      if (isCompleted(form.status)) {
        if (form.pickup_method) payload.pickup_method = form.pickup_method;
        if (form.pickup_method === "delivery") {
          payload.courier = form.courier;
          payload.tracking_number = form.tracking_number;
        }
      }
      const { data } = await api.patch(`/services/${id}`, payload);
      setSvc(data);
      toast.success("Perubahan disimpan");
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal menyimpan";
      toast.error(typeof d === "string" ? d : "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const advance = async () => {
    const next = nextStatusFlow[svc.status];
    if (!next) return;
    try {
      const { data } = await api.patch(`/services/${id}`, { status: next });
      setSvc(data);
      setForm((f) => ({ ...f, status: next }));
      toast.success(`Status → ${statusMeta[next].label}`);
    } catch {
      toast.error("Gagal mengubah status");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/services/${id}`);
      toast.success("Dihapus");
      nav("/services");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const waLink = `https://wa.me/${svc.customer_phone.replace(/\D/g, "")}?text=${encodeURIComponent(
    `Halo ${svc.customer_name}, update service ${svc.item_brand} (${svc.ticket_no}): status saat ini ${getStatusDisplay(svc).label}.`
  )}`;

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="service-detail-page">
      <div className="flex items-center gap-3 no-print">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Button>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3 no-print">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-500">{svc.ticket_no}</span>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusDisplay(svc).cls}`}
            >
              {getStatusDisplay(svc).label}
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
            {svc.customer_name}
          </h1>
          <div className="text-sm text-slate-500">
            {svc.item_brand} · {svc.item_type}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {user?.role === "technician" && svc.assigned_tech_id !== user.id && !svc.assigned_tech_id && (
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.post(`/services/${svc.id}/claim`);
                  setSvc(data);
                  toast.success(`${data.ticket_no} berhasil diambil`);
                } catch (err) {
                  const d = err?.response?.data?.detail || "Gagal";
                  toast.error(typeof d === "string" ? d : "Gagal");
                }
              }}
              data-testid="claim-service-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              Ambil Service
            </Button>
          )}
          {svc.assigned_tech_name && (
            <div className="flex items-center gap-1.5 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg">
              <span className="text-xs">Ditangani:</span>
              <span className="font-semibold">{svc.assigned_tech_name}</span>
            </div>
          )}
          <Link to={`/receipt/${svc.id}`}>
            <Button variant="outline" className="gap-2" data-testid="print-receipt-button">
              <Printer className="w-4 h-4" /> Nota
            </Button>
          </Link>
          <a href={`tel:${svc.customer_phone}`} className={role === "technician" ? "hidden" : ""}>
            <Button variant="outline" className="gap-2">
              <Phone className="w-4 h-4" /> Telepon
            </Button>
          </a>
          <a href={waLink} target="_blank" rel="noreferrer" className={role === "technician" ? "hidden" : ""}>
            <Button variant="outline" className="gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </Button>
          </a>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-service-button">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Hapus service ini?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Data yang dihapus tidak dapat dikembalikan.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Batal</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-button">
                    Hapus
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
          <h3 className="font-display font-semibold text-slate-900">Detail Barang</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pelanggan</div>
              <div className="mt-1 text-slate-900">{svc.customer_name}</div>
              {role !== "technician" && svc.customer_phone && (
                <div className="text-slate-600 text-xs">{svc.customer_phone}</div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Jenis / Merek</div>
              <div className="mt-1 text-slate-900">
                {svc.item_type} — {svc.item_brand}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Keluhan</div>
              <div className="mt-1 text-slate-800 whitespace-pre-line">{svc.complaint}</div>
            </div>
            {svc.shipping_address && (
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Alamat Pengiriman</div>
                <div className="mt-1 text-sm text-slate-800">
                  <span className="font-medium">{svc.shipping_address.label}</span>
                  {role !== "technician" && svc.shipping_address.recipient
                    ? ` · ${svc.shipping_address.recipient}`
                    : ""}
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{svc.shipping_address.full_address}</div>
                <div className="text-xs text-slate-500">{[svc.shipping_address.city, svc.shipping_address.postal_code].filter(Boolean).join(" · ")}</div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="font-display font-semibold text-slate-900 mb-3">Diagnosa & Catatan</h3>
            <div className="space-y-3">
              {canEditDiag ? (
                <div>
                  <Label>Diagnosa Teknisi</Label>
                  <Textarea
                    data-testid="detail-diagnosis"
                    value={form.diagnosis}
                    onChange={(e) => setForm((f) => ({ ...f, diagnosis: e.target.value }))}
                    rows={2}
                    placeholder="Hasil pengecekan teknisi..."
                  />
                </div>
              ) : (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Diagnosa Teknisi</div>
                  <div className="mt-1 text-slate-800 whitespace-pre-line text-sm">{svc.diagnosis || <span className="text-slate-400">-</span>}</div>
                </div>
              )}
              <div>
                <Label>Catatan</Label>
                <Textarea
                  data-testid="detail-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3">
            <h3 className="font-display font-semibold text-slate-900">Status & Biaya</h3>

            <div>
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger data-testid="detail-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="masuk">Masuk</SelectItem>
                  <SelectItem value="proses">Proses</SelectItem>
                  <SelectItem value="selesai_ok">Selesai - OK</SelectItem>
                  <SelectItem value="selesai_gagal">Selesai - Gagal</SelectItem>
                  <SelectItem value="diambil">Diambil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isCompleted(form.status) && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="pickup-section">
                <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Metode Pengambilan</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, pickup_method: "pickup" }))}
                    data-testid="pickup-self-btn"
                    className={`text-sm font-medium rounded-lg border py-2 px-3 transition-all ${form.pickup_method === "pickup" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  >
                    Ambil Sendiri
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, pickup_method: "delivery" }))}
                    data-testid="pickup-delivery-btn"
                    className={`text-sm font-medium rounded-lg border py-2 px-3 transition-all ${form.pickup_method === "delivery" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  >
                    Kirim
                  </button>
                </div>
                {form.pickup_method === "delivery" && (
                  <div className="space-y-2 pt-1">
                    <div>
                      <Label>Ekspedisi</Label>
                      <Select value={form.courier} onValueChange={(v) => setForm((f) => ({ ...f, courier: v }))}>
                        <SelectTrigger data-testid="courier-select"><SelectValue placeholder="Pilih ekspedisi" /></SelectTrigger>
                        <SelectContent>
                          {COURIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>No. Resi</Label>
                      <Input
                        data-testid="tracking-input"
                        value={form.tracking_number}
                        onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))}
                        placeholder="Isi setelah barang dikirim"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {canAssign && (
              <div>
                <Label>Teknisi</Label>
                <Select
                  value={form.assigned_tech_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, assigned_tech_id: v }))}
                >
                  <SelectTrigger data-testid="detail-tech-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Belum ditugaskan</SelectItem>
                    {techs.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Estimasi</Label>
              <div className="mt-1 text-sm text-slate-700">{formatRp(svc.estimated_cost)}</div>
            </div>

            {canEditCost ? (
              <div>
                <Label htmlFor="final_cost">Biaya Final (Rp)</Label>
                <Input
                  id="final_cost"
                  type="number"
                  min="0"
                  data-testid="detail-final-cost"
                  value={form.final_cost}
                  onChange={(e) => setForm((f) => ({ ...f, final_cost: e.target.value }))}
                  placeholder="0"
                />
              </div>
            ) : svc.final_cost > 0 ? (
              <div>
                <Label>Biaya Final</Label>
                <div className="mt-1 text-sm font-semibold text-slate-800">{formatRp(svc.final_cost)}</div>
              </div>
            ) : null}

            <Button
              onClick={save}
              disabled={saving}
              data-testid="detail-save-button"
              className="w-full bg-slate-900 hover:bg-slate-800 gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>

            {nextStatusFlow[svc.status] && (
              <Button
                onClick={advance}
                variant="outline"
                data-testid="detail-advance-status"
                className="w-full gap-2"
              >
                Lanjut ke {statusMeta[nextStatusFlow[svc.status]].label} →
              </Button>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Masuk</span>
              <span className="text-slate-800">{formatDate(svc.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Update</span>
              <span className="text-slate-800">{formatDate(svc.updated_at)}</span>
            </div>
            {svc.completed_at && (
              <div className="flex justify-between">
                <span className="text-slate-500">Selesai</span>
                <span className="text-slate-800">{formatDate(svc.completed_at)}</span>
              </div>
            )}
            {svc.picked_up_at && (
              <div className="flex justify-between">
                <span className="text-slate-500">Diambil</span>
                <span className="text-slate-800">{formatDate(svc.picked_up_at)}</span>
              </div>
            )}
            {svc.assigned_tech_name && (
              <div className="flex justify-between border-t border-slate-100 pt-2">
                <span className="text-slate-500">Teknisi</span>
                <span className="text-slate-800">{svc.assigned_tech_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <PhotoGallery serviceId={svc.id} />

      <AuditLog serviceId={svc.id} />
    </div>
  );
}
